import {
  SESSION_INTERFACE_IDS,
  createActorParticipantId,
  createUserParticipantId,
  normalizeSessionInterfaceId,
} from '../domain'

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      return value
    }
  }

  return null
}

function normalizeEventType(value) {
  return normalizeOptionalString(value, '')?.toLowerCase() || ''
}

function resolvePractitionerRole(eventType) {
  const normalizedEventType = normalizeEventType(eventType)

  if (normalizedEventType.includes('consult')) {
    return 'doctor'
  }

  return 'mentor'
}

function resolvePractitionerRoleLabel(eventType) {
  const normalizedEventType = normalizeEventType(eventType)

  if (normalizedEventType.includes('consult')) {
    return 'Doctor'
  }

  if (normalizedEventType.includes('therapy')) {
    return 'Therapist'
  }

  if (normalizedEventType.includes('screen')) {
    return 'Practitioner'
  }

  return 'Mentor'
}

function resolveFallbackSessionInterfaceId(flowType) {
  switch (flowType) {
    case 'public-live-session':
      return SESSION_INTERFACE_IDS.publicLive
    case 'mentor-chamber-auto-join':
      return SESSION_INTERFACE_IDS.mentorChamber
    case 'chamber-screen':
    case 'kiosk-screen':
      return SESSION_INTERFACE_IDS.kioskScreen
    case 'audit-session':
      return SESSION_INTERFACE_IDS.auditObserver
    case 'mentor-live-session':
    case 'consumer-live-session':
    default:
      return SESSION_INTERFACE_IDS.standardLive
  }
}

export function readSessionInterfaceId(source, fallbackFlowType) {
  const explicitSessionInterfaceId = normalizeOptionalString(
    source?.sessionInterfaceId ??
      source?.sessionInterface?.id ??
      source?.data?.sessionInterfaceId ??
      source?.data?.data?.sessionInterfaceId,
  )

  return normalizeSessionInterfaceId(
    explicitSessionInterfaceId,
    resolveFallbackSessionInterfaceId(fallbackFlowType),
  )
}

export function readLocalParticipantIdentity(source, fallbackIdentity = null) {
  const explicitIdentity =
    source?.localParticipantIdentity ??
    source?.data?.localParticipantIdentity ??
    source?.data?.data?.localParticipantIdentity ??
    null

  if (isPlainObject(explicitIdentity)) {
    return explicitIdentity
  }

  return isPlainObject(fallbackIdentity) ? fallbackIdentity : null
}

export function createBookedEventLocalParticipantIdentity(bookedEvent, userRole) {
  if (!isPlainObject(bookedEvent)) {
    return null
  }

  if (userRole === 'mentor' || userRole === 'panelist' || userRole === 'support') {
    return null
  }

  const userId = normalizeOptionalString(bookedEvent?.userId)

  if (!userId) {
    return null
  }

  return {
    participantId: createUserParticipantId(userId),
    userId,
    actorType: 'user',
    actorId: userId,
    displayName: normalizeOptionalString(
      bookedEvent?.patientName ?? bookedEvent?.userName,
      'Participant',
    ),
    profilePicId: normalizeOptionalString(
      bookedEvent?.profilePicId ??
        bookedEvent?.patientProfilePicId ??
        bookedEvent?.userProfilePicId,
    ),
    role: userRole || 'user',
    isHuman: true,
  }
}

export function createPublicLocalParticipantIdentity(joinData) {
  if (!isPlainObject(joinData)) {
    return null
  }

  const userId = normalizeOptionalString(
    joinData?.userId ?? joinData?.bookedEvent?.userId,
  )

  if (!userId) {
    return null
  }

  return {
    participantId: createUserParticipantId(userId),
    userId,
    actorType: 'user',
    actorId: userId,
    displayName: normalizeOptionalString(
      pickFirst(joinData?.eventUserName, joinData?.patientName, joinData?.userName),
      'Patient',
    ),
    profilePicId: normalizeOptionalString(
      pickFirst(
        joinData?.profilePicId,
        joinData?.userProfilePicId,
        joinData?.bookedEvent?.profilePicId,
        joinData?.bookedEvent?.userProfilePicId,
      ),
    ),
    role: 'patient',
    isHuman: true,
  }
}

export function createBookedEventPeerParticipantHint(
  bookedEvent,
  userRole,
  options = {},
) {
  if (!isPlainObject(bookedEvent)) {
    return null
  }

  const normalizedUserRole = normalizeOptionalString(userRole, '')?.toLowerCase() || ''
  const localIsPractitioner =
    normalizedUserRole === 'mentor' ||
    normalizedUserRole === 'doctor' ||
    normalizedUserRole === 'panelist' ||
    normalizedUserRole === 'support' ||
    normalizedUserRole === 'auditor'

  if (localIsPractitioner) {
    const peerUserId = normalizeOptionalString(
      options?.peerUserId ?? bookedEvent?.userId,
    )

    return {
      participantId: peerUserId ? createUserParticipantId(peerUserId) : null,
      userId: peerUserId,
      actorType: peerUserId ? 'user' : null,
      actorId: peerUserId,
      displayName: normalizeOptionalString(
        options?.peerDisplayName ??
          bookedEvent?.patientName ??
          bookedEvent?.userName,
        'Patient',
      ),
      profilePicId: normalizeOptionalString(
        options?.peerProfilePicId ??
          bookedEvent?.profilePicId ??
          bookedEvent?.patientProfilePicId ??
          bookedEvent?.userProfilePicId,
      ),
      role: 'patient',
      roleLabel: 'Patient',
      isHuman: true,
    }
  }

  const peerUserId = normalizeOptionalString(
    options?.peerUserId ??
      bookedEvent?.mentorUserId ??
      bookedEvent?.mentor?.userId,
  )
  const role = resolvePractitionerRole(bookedEvent?.eventType)

  return {
    participantId: peerUserId ? createUserParticipantId(peerUserId) : null,
    userId: peerUserId,
    actorType: peerUserId ? 'user' : null,
    actorId: peerUserId,
    displayName: normalizeOptionalString(
      options?.peerDisplayName ?? bookedEvent?.mentorName,
      role === 'doctor' ? 'Doctor' : 'Mentor',
    ),
    profilePicId: normalizeOptionalString(
      options?.peerProfilePicId ??
        bookedEvent?.mentorProfilePicId ??
        bookedEvent?.mentorProfilePic,
    ),
    role,
    roleLabel: resolvePractitionerRoleLabel(bookedEvent?.eventType),
    isHuman: true,
  }
}

export function createPublicPeerParticipantHint(joinData, options = {}) {
  if (!isPlainObject(joinData)) {
    return null
  }

  const peerUserId = normalizeOptionalString(
    options?.peerUserId ??
      joinData?.mentorUserId ??
      joinData?.mentor?.userId,
  )
  const role = resolvePractitionerRole(joinData?.eventType)

  return {
    participantId: peerUserId ? createUserParticipantId(peerUserId) : null,
    userId: peerUserId,
    actorType: peerUserId ? 'user' : null,
    actorId: peerUserId,
    displayName: normalizeOptionalString(
      options?.peerDisplayName ?? joinData?.mentorName,
      role === 'doctor' ? 'Doctor' : 'Mentor',
    ),
    profilePicId: normalizeOptionalString(
      options?.peerProfilePicId ??
        joinData?.mentorProfilePicId ??
        joinData?.mentorProfilePic,
    ),
    role,
    roleLabel: resolvePractitionerRoleLabel(joinData?.eventType),
    isHuman: true,
  }
}

export function createMentorChamberLocalParticipantIdentity(joinData, mentorProfileData = null) {
  if (!isPlainObject(joinData) && !isPlainObject(mentorProfileData)) {
    return null
  }

  const userId = normalizeOptionalString(
    joinData?.userId ??
      joinData?.mentorUserId ??
      joinData?.mentor?.userId ??
      mentorProfileData?.userId,
  )

  if (!userId) {
    return null
  }

  return {
    participantId: createUserParticipantId(userId),
    userId,
    actorType: 'user',
    actorId: userId,
    displayName: normalizeOptionalString(
      joinData?.displayName ?? joinData?.mentorName ?? mentorProfileData?.name,
      'Mentor',
    ),
    profilePicId: normalizeOptionalString(
      joinData?.profilePicId ??
        joinData?.mentorProfilePicId ??
        mentorProfileData?.profilePicId ??
        mentorProfileData?.profilePictureId,
    ),
    role: normalizeOptionalString(joinData?.role, 'mentor'),
    isHuman: true,
  }
}

export function createChamberScreenLocalParticipantIdentity(
  joinData,
  fallbackActorId = null,
) {
  if (!isPlainObject(joinData) && !fallbackActorId) {
    return null
  }

  const actorId = normalizeOptionalString(
    joinData?.screenId ??
      joinData?.screenDeviceConfigId ??
      joinData?.chamberScreenId ??
      fallbackActorId,
  )

  if (!actorId) {
    return null
  }

  return {
    participantId: createActorParticipantId('chamber-screen', actorId),
    actorType: 'chamber-screen',
    actorId,
    displayName: normalizeOptionalString(
      joinData?.displayName,
      'Chamber Screen',
    ),
    profilePicId: normalizeOptionalString(
      joinData?.profilePicId ?? joinData?.screenProfilePicId,
    ),
    role: normalizeOptionalString(joinData?.role, 'screen'),
    isHuman: false,
  }
}
